import { clipBoardPayloadDecode } from '../lib/clipboard';
import { TYPES } from '../render/constants';
import { Action } from './action';
import { copyActionHandlerSync, pasteNodesSync } from './copy';

function condition() {
  return true;
}

export const expandFrameAction = new Action({
  name: 'ExpandFrame',
  handler: expandFrame,
  shortcuts: 'Ctrl+E',
  condition: condition,
});

export function isFrameOverflow(stageContext, frame) {
  if (!frame || frame.type !== TYPES.frame) return false;

  const children = stageContext.api
    .getChildren(frame.id)
    .filter((n) => n && n.visible !== false && n.hidden !== true);

  if (!children.length) return false;

  const frameRect = stageContext.api.getNodeRegion(frame).rect;
  const frameBottom = frameRect.y + frameRect.h;
  const frameRight = frameRect.x + frameRect.w;

  // Check if any child overflows the frame boundaries
  return children.some((child) => {
    const childRegion = stageContext.api.getNodeRegion(child);
    if (!childRegion) return false;

    const rect = childRegion.rect;
    const childBottom = rect.y + rect.h;
    const childRight = rect.x + rect.w;

    // Check overflow in any direction
    return (
      childBottom > frameBottom ||
      childRight > frameRight ||
      rect.y < frameRect.y ||
      rect.x < frameRect.x
    );
  });
}

export function expandFrame(stageContext, frame) {
  const selectedNodes = stageContext.api.getSelectedNodes();
  frame = frame || selectedNodes.find((n) => n.type === TYPES.frame);
  if (!frame || frame.type !== TYPES.frame) return;

  const children = stageContext.api
    .getChildren(frame.id)
    .filter((n) => n && n.visible !== false && n.hidden !== true);

  if (!children.length) return;

  const frameRect = stageContext.api.getNodeRegion(frame).rect;

  let maxBottom = frameRect.y + frameRect.h;
  const safePadding = 48;

  // Find background shapes that currently match the frame size (w/h一致)
  const matchingBackgroundShapes = children.filter(
    (n) => n.type === TYPES.shape && n.w === frame.w && n.h === frame.h,
  );

  children.forEach((child) => {
    const childRegion = stageContext.api.getNodeRegion(child);
    if (!childRegion) return;
    const rect = childRegion.rect;
    const bottom = rect.y + rect.h;
    if (bottom > maxBottom) maxBottom = bottom;
  });

  const overflowBottom = maxBottom - (frameRect.y + frameRect.h);
  if (overflowBottom > 0) {
    const newHeight = frame.h + overflowBottom + safePadding;
    stageContext.api.updateNode(frame.id, { h: newHeight });

    // 同步更新与 frame 宽高一致的背景 shape 的高度
    matchingBackgroundShapes.forEach((shape) => {
      stageContext.api.updateNode(shape.id, { h: newHeight });
    });
  }
}

export const splitFrameAction = new Action({
  name: 'SplitFrameSections',
  handler: splitFrameSections,
  condition: condition,
});

export function splitFrameSections(stageContext, frame) {
  const MAX_SECTION_HEIGHT = 1500;
  const SAFE_PADDING = 48;

  const selectedNodes = stageContext.api.getSelectedNodes();
  frame = frame || selectedNodes.find((n) => n.type === TYPES.frame);
  if (!frame || frame.type !== TYPES.frame) return;

  const allChildren = stageContext.api
    .getChildren(frame.id)
    .filter((n) => n && n.visible !== false && n.hidden !== true);

  if (!allChildren.length) return;

  // 识别与 frame 同宽高的背景 shape，不参与内容分段
  const backgroundShapes = allChildren.filter(
    (n) => n.type === TYPES.shape && n.w === frame.w && n.h === frame.h,
  );
  const bgShapeIdSet = new Set(backgroundShapes.map((s) => s.id));

  const contentItems = allChildren
    .filter((n) => !bgShapeIdSet.has(n.id))
    .map((n) => {
      const region = stageContext.api.getNodeRegion(n);
      if (!region) return null;
      return { node: n, rect: region.rect };
    })
    .filter(Boolean)
    .sort((a, b) => a.rect.y - b.rect.y);

  if (!contentItems.length) {
    // 只有背景，直接限制到 MAX_SECTION_HEIGHT 即可
    const newHeight = Math.min(frame.h, MAX_SECTION_HEIGHT);
    stageContext.api.updateNode(frame.id, { h: newHeight });
    backgroundShapes.forEach((shape) => {
      stageContext.api.updateNode(shape.id, { x: 0, y: 0, w: frame.w, h: newHeight });
    });
    return;
  }

  // 基于内容的分段：按 rect.y 从上到下聚合，保证每段高度（含上下内边距）不超过 MAX_SECTION_HEIGHT
  const sections = [];
  const sectionPadding = SAFE_PADDING * 2;
  let currentNodes = [];
  let currentTop = null;
  let currentBottom = null;

  for (const item of contentItems) {
    const nodeTop = item.rect.y;
    const nodeBottom = item.rect.y + item.rect.h;

    if (currentNodes.length === 0) {
      currentNodes.push(item);
      currentTop = nodeTop;
      currentBottom = nodeBottom;
      continue;
    }

    if (
      nodeTop >= currentTop &&
      (nodeBottom <= currentTop + MAX_SECTION_HEIGHT - sectionPadding ||
        nodeBottom <= currentBottom)
    ) {
      currentNodes.push(item);
      currentBottom = Math.max(nodeBottom, currentBottom);
    } else {
      sections.push({ items: currentNodes });
      currentNodes = [item];
      currentTop = nodeTop;
      currentBottom = nodeBottom;
    }
  }

  if (currentNodes.length) sections.push({ items: currentNodes });

  // 计算插入顺序：新 section frame 紧随原 frame 之后
  const frames = stageContext.api.getFrames();
  const frameIndex = frames.findIndex((f) => f.id === frame.id);
  const nextIndex = frames[frameIndex + 1]?.index || null;
  let lastIndex = frame.index;

  let yCursor = frame.y + frame.h + 80;

  sections.forEach((section, idx) => {
    const sectionTopGlobal = Math.min(...section.items.map((it) => it.rect.y));
    const sectionBottomGlobal = Math.max(...section.items.map((it) => it.rect.y + it.rect.h));
    let sectionHeight = Math.round(sectionBottomGlobal - sectionTopGlobal + SAFE_PADDING * 2);

    // 如果单个节点本身高度超过 MAX_SECTION_HEIGHT，则允许该段超过限制以避免截断
    const isSingleOversize =
      section.items.length === 1 && section.items[0].rect.h + SAFE_PADDING * 2 > MAX_SECTION_HEIGHT;
    if (!isSingleOversize) {
      sectionHeight = Math.min(sectionHeight, MAX_SECTION_HEIGHT);
    }

    // 为本段创建新 frame（包括第一段）
    const newFrame = stageContext.api.addNode({
      type: TYPES.frame,
      name: `${frame.name || 'Frame'} - ${idx + 1}`,
      x: frame.x,
      y: yCursor,
      w: frame.w,
      h: sectionHeight,
      fill: frame.fill,
    });

    if (!newFrame || !newFrame.id) {
      console.warn('[splitFrameSections] fail to create new frame for section', idx);
      return;
    }

    // 调整层级顺序：确保在原 frame 之后
    lastIndex = stageContext.api.generateKeyBetween(lastIndex, nextIndex);
    stageContext.api.updateNode(newFrame.id, { index: lastIndex });

    // 为新 frame 追加背景 shape 的副本
    backgroundShapes.forEach((shape) => {
      const clone = { ...shape };
      delete clone.id;
      clone.parentId = newFrame.id;
      clone.x = 0;
      clone.y = 0;
      clone.w = frame.w;
      clone.h = sectionHeight;
      stageContext.api.addNode(clone);
    });

    // 复制该段内容到新 frame（不移动来源，避免截断）
    // 仅复制该段内的根节点（避免重复复制子级）
    const sectionNodeIdSet = new Set(section.items.map((it) => it.node.id));
    const sectionRoots = section.items
      .map((it) => it.node)
      .filter((n) => !sectionNodeIdSet.has(n.parentId));

    // 复制
    const text = copyActionHandlerSync(stageContext, sectionRoots, {}, false);
    const payload = clipBoardPayloadDecode(text);
    payload.selectedNodes = [newFrame];
    const newNodes = pasteNodesSync(stageContext, payload, { keepOriginOffset: true }) || [];

    // 本段内顶层节点的最小本地 y，用于消除顶部空白
    const localTopY = Math.min(...sectionRoots.map((n) => n.y));
    newNodes.forEach((n) => {
      stageContext.api.updateNode(n.id, { y: Math.max(0, (n.y || 0) - localTopY) });
    });

    // 推进游标
    yCursor += sectionHeight + 80;
  });

  stageContext.hooks.onFrameChange?.call();
}
